import { useState } from 'react';
import axios from 'axios';
import { Feature } from './Feature';
import { ClientType } from './ClientType';
import { Client } from './Client';

// Define your base URL here
//const BASE_URL = 'https://localhost:7286/api/';
const BASE_URL = process.env.REACT_APP_API_BASE;
const API_KEY = process.env.REACT_APP_API_KEY;

export function useApi<T = any>() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<T | null>(null);

  const callApi = async (endpoint: string, method: 'get' | 'post' = 'get', body?: any) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios({
        url: `${BASE_URL}${endpoint}`,
        method,
        data: body,
        headers: {
          'X-API-Key': API_KEY,
        },
      });
      setData(response.data);
    } catch (err: any) {
      console.error('API Error:', err); // Debugging log
      setError(err.message || 'API error');
    } finally {
      setLoading(false);
    }
  };

  return { loading, error, data, callApi };
}

// Method to call the API and return a list of Features
export async function fetchFeatures(): Promise<Feature[]> {
    console.log('BASE_URL:', BASE_URL);
    //  console.log('API_KEY:', API_KEY);
  const response = await axios.get(`${BASE_URL}feature`, {
    headers: {
      'X-API-Key': API_KEY,
    },
  });
  // Map each item in the response to a Feature instance
  return response.data.map((item: any) => new Feature(item));
}

// Method to call the API and return a list of Features
export async function fetchClientTypes(): Promise<ClientType[]> {
    console.log('BASE_URL:', BASE_URL);
     //console.log('API_KEY:', API_KEY);
  const response = await axios.get(`${BASE_URL}clienttype`, {
    headers: {
      'X-API-Key': API_KEY,
    },
  });
  // Map each item in the response to a Feature instance
  return response.data.map((item: any) => new ClientType(item));
}

// Method to call the API and return a list of Features
export async function fetchNearByClients(
  latitude: number,
  longitude: number,
  radiusKm: number = 10
): Promise<Client[]> {
  const response = await axios.get(`${BASE_URL}client/nearby-geography`, {
    params: {
      latitude,
      longitude,
      radiusKm,
    },
    headers: {
      'X-API-Key': API_KEY,
    },
  });

  console.log('API response:', response);
  return response.data.map((item: any) => new Client(item));
}

// Method to call the API and return a list of Features
export async function fetchNearByClients2(
  southWestLat: number,
  southWestLng: number,
  northEastLat: number,
  northEastLng: number,
  clientTypeIds: string[] | null = null,
  featureIds: string[] | null = null
): Promise<Client[]> {
  const requestBody = {
    SouthWestLat: southWestLat,
    SouthWestLng: southWestLng,
    NorthEastLat: northEastLat,
    NorthEastLng: northEastLng,
    ClientTypeIds: clientTypeIds,
    FeatureIds: featureIds,
  };

  console.log('Request Body:', requestBody);

  const response = await axios.post(`${BASE_URL}client/in-bounds`, requestBody, {    
    headers: {
      'X-API-Key': API_KEY,
    },
  });

  console.log('API response:', response);
  return response.data.map((item: any) => new Client(item));
}

export async function submitLocationRequest(data: {
  name: string;
  description: string;
  email: string;
  contactEmail: string;
  website: string;
  phone: string;
  latitudeLongitudeString: string;
  clientTypeID: string;
  clientRequestFeatureIDs: string[];
}): Promise<void> {
  try {

    console.log('Request Body:', data);

    const response = await axios.post(`${BASE_URL}ClientRequest`, data, {
      headers: {
        'X-API-Key': API_KEY,
        'Content-Type': 'application/json',
      },
    });
  } catch (error: any) {
    console.error('Error submitting location request:', error.response?.data || error.message);
    throw new Error(error.response?.data?.message || 'Failed to submit location request');
  }
}

export interface ClientRequestFeatureDto {
  id: number;
  featureID: number;
  name: string;
  description: string | null;
}

export interface PendingClientRequestDto {
  clientRequestID: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  clientTypeID: number;
  clientTypeName: string;
  website: string | null;
  email: string | null;
  contactEmail: string | null;
  phone: string | null;
  createdDate: string;
  modifiedDate: string | null;
  clientRequestFeatures: ClientRequestFeatureDto[];
}

export interface PendingClientRequestsResponseDto {
  totalCount: number;
  data: PendingClientRequestDto[];
}

export async function fetchPendingClientRequests(
  pageSize: number,
  pageIndex: number,
  password: string
): Promise<PendingClientRequestsResponseDto> {
  const response = await axios.get(`${BASE_URL}ClientRequest/PendingClientRequests`, {
    params: {
      password,
      PageSize: pageSize,
      PageIndex: pageIndex,
    },
    headers: {
      'X-API-Key': API_KEY,
      'X-Api-EditPassword': password,
    },
  });

  return response.data;
}

export async function createClientFromRequest(
    clientRequestId: string,
    password: string,
    verified: boolean = false
): Promise<void> {
  await axios.post(`${BASE_URL}Client/from-request/${clientRequestId}`, null, {
    params: {
      verified,
    },
    headers: {
      'X-API-Key': API_KEY,
      'X-Api-EditPassword': password,
    },
  });
}

export interface BlogArticleShortDto {
  blogArticleID: string;
  title: string;
  summary: string;
  createdDate: string;
}

export interface BlogArticleDto {
  blogArticleID: string;
  title: string;
  author: string;
  summary: string;
  content: string;
  isPublished: boolean;
  createdDate: string;
  modifiedDate?: string | null;
}

export interface BlogArticlePagedResponseDto {
  totalCount: number;
  data: BlogArticleShortDto[];
}

export interface CreateBlogArticleDto {
  title: string;
  author: string;
  summary: string;
  content: string;
  isPublished: boolean;
}

export interface UpdateBlogArticleDto {
  blogArticleID: string;
  title: string;
  author: string;
  summary: string;
  content: string;
  isPublished: boolean;
}

export async function fetchBlogArticles(
  pageSize: number = 10,
  pageIndex: number = 1,
  keyword: string = ''
): Promise<BlogArticlePagedResponseDto> {
  const response = await axios.get(`${BASE_URL}BlogArticle`, {
    params: {
      pageSize,
      pageIndex,
      keyword: keyword || undefined,
    },
    headers: {
      'X-API-Key': API_KEY,
    },
  });

  return response.data;
}

export async function fetchBlogArticle(articleId: string): Promise<BlogArticleDto> {
  const response = await axios.get(`${BASE_URL}BlogArticle/${articleId}`, {
    headers: {
      'X-API-Key': API_KEY,
    },
  });

  return response.data;
}

export async function createBlogArticle(
  payload: CreateBlogArticleDto,
  password: string
): Promise<BlogArticleDto> {
  const response = await axios.post(`${BASE_URL}BlogArticle`, payload, {
    headers: {
      'X-API-Key': API_KEY,
      'X-Api-EditPassword': password,
      'Content-Type': 'application/json',
    },
  });

  return response.data;
}

export async function updateBlogArticle(
  payload: UpdateBlogArticleDto,
  password: string
): Promise<void> {
  await axios.put(`${BASE_URL}BlogArticle`, payload, {
    headers: {
      'X-API-Key': API_KEY,
      'X-Api-EditPassword': password,
      'Content-Type': 'application/json',
    },
  });
}